import React, { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  Button,
  Col,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from "reactstrap";
import CommonPagination from "../../../components/Common/TableView/CommonPagination";
import TableHeader from "../../../components/Common/TableView/TableHeader";
import {
  ActivityLogSvg,
  VendorTrashSvg,
} from "../../../components/CommonSvg";
import { commonConfirmBox } from "../../../helper/messagehelpers";
import {
  USER_LOG_TYPE_MEMBER,
  USER_LOG_TYPE_STAFF,
  userLogDuration,
} from "../../../constant/commonConstants";
import { getInitials } from "../../../utils/generalUtils";
import {
  clearUserLogList,
  deleteUserLogRequest,
  getUserLogListRequest,
} from "../../../redux/admin/userLog/action";
import { exportDataExportRequest } from "../../../redux/exportData/action";

const EMPTY_FILTER = {
  fromDate: null,
  toDate: null,
};

/* One tab per kind of account, because user_logs keeps them apart too:
   usertype "S" is a portal user, "M" a member. */
const TABS = [
  { key: "1", userType: USER_LOG_TYPE_STAFF, label: "Admin User" },
  { key: "2", userType: USER_LOG_TYPE_MEMBER, label: "Members" },
];

/* Every sign-in recorded in user_logs, newest first.
 *
 * This used to be reached per row, from an icon on the user and member lists.
 * It is one screen now, so the rows carry who they belong to - user_logs has no
 * foreign key, only the e-mail or HRA number the session was opened with, and
 * the API resolves the name for the rows on the page. */
function UserLogs({
  getUserLogList,
  deleteUserLog,
  clearUserLogs,
  exportUserLogs,
  userLogList,
  totalRecords,
  isLoading,
}) {
  const [activeTab, setActiveTab] = useState("1");
  const [currentPage, setCurrentPage] = useState(1);
  const [dataPerPage, setDataPerPage] = useState(10);
  const [searchData, setSearchData] = useState("");
  const [appliedFilter, setAppliedFilter] = useState(EMPTY_FILTER);

  const userType =
    TABS.find((tab) => tab.key === activeTab)?.userType || USER_LOG_TYPE_STAFF;

  const listData = useMemo(
    () => ({
      userType: userType,
      searchText: searchData,
      fromDate: appliedFilter.fromDate
        ? moment(appliedFilter.fromDate).format("YYYY-MM-DD")
        : "",
      toDate: appliedFilter.toDate
        ? moment(appliedFilter.toDate).format("YYYY-MM-DD")
        : "",
      pagination: {
        limit: dataPerPage,
        page: currentPage,
      },
    }),
    [userType, searchData, appliedFilter, dataPerPage, currentPage]
  );

  useEffect(() => {
    getUserLogList(listData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listData]);

  useEffect(() => {
    return () => clearUserLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* switching tab is a different list entirely - page and filters would carry
     a meaning they no longer have */
  const toggleTab = (key) => {
    if (key === activeTab) {
      return;
    }
    setActiveTab(key);
    setCurrentPage(1);
    setSearchData("");
    setAppliedFilter(EMPTY_FILTER);
  };

  const isFiltered =
    !!searchData || !!appliedFilter.fromDate || !!appliedFilter.toDate;
  const isBlank = !isLoading && !isFiltered && totalRecords === 0;

  const hasRange = !!(appliedFilter.fromDate && appliedFilter.toDate);
  const hasSearch = !!searchData;
  /* clearing is always scoped: without a range or a search there is nothing
     narrowing the list, and the whole history would go */
  const canDelete = hasRange || hasSearch;

  const deleteScopeText = () => {
    const range = hasRange
      ? `between ${moment(appliedFilter.fromDate).format(
          "MM-DD-YYYY"
        )} and ${moment(appliedFilter.toDate).format("MM-DD-YYYY")}`
      : "";
    const search = hasSearch ? `matching "${searchData}"` : "";
    const tab = TABS.find((item) => item.key === activeTab)?.label || "";
    return [tab ? `of ${tab}` : "", range, search]
      .filter(Boolean)
      .join(" and ");
  };

  const handleDeleteFiltered = async () => {
    if (!canDelete) {
      return;
    }
    const confirmed = await commonConfirmBox(
      "Clear the sign-in history shown here?",
      "Yes, clear it",
      "Cancel",
      `Every sign-in record ${deleteScopeText()} will be permanently deleted. This cannot be undone.`
    );
    if (confirmed) {
      setCurrentPage(1);
      deleteUserLog({
        userType: userType,
        fromDate: hasRange
          ? moment(appliedFilter.fromDate).format("YYYY-MM-DD")
          : "",
        toDate: hasRange
          ? moment(appliedFilter.toDate).format("YYYY-MM-DD")
          : "",
        searchText: hasSearch ? searchData : "",
        listData: listData,
      });
    }
  };

  const trail = (
    <div className="vendor_log_wrap">
      {isLoading ? (
        <div className="vendor_log_skeleton">
          {[0, 1, 2, 3, 4].map((row) => (
            <div className="vendor_log_skeleton_row" key={row}>
              <span className="vendor_log_skeleton_avatar" />
              <span className="vendor_log_skeleton_line" />
              <span className="vendor_log_skeleton_line short" />
            </div>
          ))}
        </div>
      ) : userLogList.length > 0 ? (
        <div className="table-responsive">
          <table className="table vendor_log_table">
            <thead>
              <tr>
                <th>User</th>
                <th>Signed In</th>
                <th>Signed Out</th>
                <th>Duration</th>
                <th>Session</th>
              </tr>
            </thead>
            <tbody>
              {userLogList.map((log) => (
                <tr key={log.id}>
                  <td>
                    <div className="vendor_log_user_cell">
                      <span className="vendor_log_avatar">
                        {getInitials(log.name) || "?"}
                      </span>
                      <div>
                        <span className="vendor_log_user">
                          {/* the account can be renamed or removed after the
                              login; what it signed in with never changes */}
                          {log.name || log.userID || "Unknown user"}
                          {/* What the session was actually opened with. For a
                              member that is the HRA number, worth showing on
                              its own; for a portal user it is the e-mail, which
                              is already on the line below - so it is shown only
                              when it says something the e-mail does not. */}
                          {log.userID && log.userID !== log.email && (
                            <span className="vendor_log_hra">{log.userID}</span>
                          )}
                        </span>
                        <span className="vendor_log_email">
                          {log.email || log.userID || ""}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="vendor_log_time">
                    {log.loginDate
                      ? moment(log.loginDate).format("MM-DD-YYYY hh:mm A")
                      : "-"}
                  </td>
                  <td className="vendor_log_time">
                    {log.logoutDate
                      ? moment(log.logoutDate).format("MM-DD-YYYY hh:mm A")
                      : "-"}
                  </td>
                  <td className="vendor_log_reference">
                    {userLogDuration(log.durationSeconds)}
                  </td>
                  <td>
                    <span
                      className={`vendor_log_action log-${
                        log.isActive ? "login" : "logout"
                      }`}
                    >
                      {log.isActive ? "Still signed in" : "Signed out"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="vendor_log_empty">
          <span className="vendor_log_empty_icon">
            <ActivityLogSvg />
          </span>
          <h6>{isFiltered ? "No matching activity" : "No activity yet"}</h6>
          <p>
            {isFiltered
              ? "No sign-in matches the search or date range you applied."
              : "Nothing has been recorded here so far."}
          </p>
          {isFiltered && (
            <Button
              className="vendor_log_empty_reset"
              onClick={() => {
                setCurrentPage(1);
                setSearchData("");
                setAppliedFilter(EMPTY_FILTER);
              }}
            >
              Reset filters
            </Button>
          )}
        </div>
      )}

      {totalRecords > 0 && (
        <CommonPagination
          dataPerPage={dataPerPage}
          totalData={totalRecords}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          setDataPerPage={setDataPerPage}
          direction="up"
        />
      )}
    </div>
  );

  return (
    <div className="container">
      <TableHeader
        hedingName={"User Logs"}
        searchBar={true}
        dataPerPage={dataPerPage}
        currentPage={currentPage}
        updateData={() => {}}
        setSearchData={(text) => {
          setCurrentPage(1);
          setSearchData(text || "");
        }}
        exportData={totalRecords > 0}
        exportDataHandel={() => {
          /* the list is paginated, the export is not - it takes the same scope
             the table is showing, and isFilteredDataExport tells the API
             whether that scope is narrowed at all */
          exportUserLogs({
            type: "POST",
            url: "/userLogDataExport",
            data: { ...listData, isFilteredDataExport: isFiltered },
          });
        }}
        Filter={!isBlank}
        filterType={"userLog"}
        logFilter={appliedFilter}
        setLogFilter={(next) => {
          setCurrentPage(1);
          setAppliedFilter((prev) => ({ ...prev, ...next }));
        }}
        logFilterActions={
          canDelete &&
          totalRecords > 0 && (
            <button
              className="vendor_log_delete_btn"
              onClick={handleDeleteFiltered}
            >
              <VendorTrashSvg size={14} />
              Delete Activity
            </button>
          )
        }
      />

      <Row>
        <Col className="d-flex justify-content-between align-items-end">
          <Nav tabs className="promotion_tab request_tab">
            {/* labels come from TABS, so the tab and the list it asks for can
                never drift apart */}
            {TABS.map((tab) => (
              <NavItem key={tab.key}>
                <NavLink
                  className={activeTab === tab.key ? "active" : ""}
                  onClick={() => toggleTab(tab.key)}
                >
                  <span>{tab.label}</span>
                </NavLink>
              </NavItem>
            ))}
          </Nav>
        </Col>
      </Row>

      <TabContent activeTab={activeTab} className="promotion_tab-content">
        <TabPane tabId={activeTab}>{trail}</TabPane>
      </TabContent>
    </div>
  );
}

const mapStateToProps = ({ adminUserLog }) => {
  const userLogList =
    adminUserLog?.getUserLogs?.list?.length > 0
      ? adminUserLog.getUserLogs.list
      : [];
  const totalRecords = adminUserLog?.getUserLogs?.totalRecords || 0;
  const isLoading = !adminUserLog?.getUserLogs;
  return { userLogList, totalRecords, isLoading };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getUserLogList: (data) => dispatch(getUserLogListRequest(data)),
    deleteUserLog: (data) => dispatch(deleteUserLogRequest(data)),
    clearUserLogs: () => dispatch(clearUserLogList()),
    exportUserLogs: (data) => dispatch(exportDataExportRequest(data)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(UserLogs);
